import { Dispatch, forwardRef, SetStateAction, useRef, useState } from 'react';
import { css, styled } from 'styled-components';
import DatePicker, { CalendarContainer, ReactDatePickerCustomHeaderProps, registerLocale } from 'react-datepicker';
import { sv } from 'date-fns/locale';
import { flip } from '@floating-ui/react';

import 'react-datepicker/dist/react-datepicker.css';
import { theme } from '../../styles';
import { ErrorMessage, InputField, Label } from '../Form';
import { Text } from '../Text';
import { CalendarIcon, ChevronIconOld } from '../../assets/icons';
import { Colors, createResponsiveSpacingStyle, getColor, ResponsiveSpacing } from '../../utils';
import { Animate } from '../Animate';
import { useMediaMin } from '../Media';
import { Box } from '../Layout';
import { Button } from '../Button';

registerLocale('sv', sv);

const Wrapper = styled.div<{
  showClearButton: boolean;
  hasErrors?: boolean;
  width?: ResponsiveSpacing;
  useUntilFurtherNotice: boolean;
}>`
  @media screen and (max-width: ${theme.breakpoint.md}px) {
    width: 100%;
  }

  & .react-datepicker {
    font-family:
      'Myriad',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      sans-serif;
    box-shadow: 0px 1px 0px 0px rgba(135, 135, 135, 1);
    -webkit-box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
    -moz-box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
    border: none !important;
    border-radius: 0;
    font-size: 0.875rem;
    font-weight: 600;
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
  }

  & .react-datepicker-wrapper {
    width: 100%;
  }

  & .react-datepicker__triangle {
    display: none;
  }

  & .react-datepicker__header {
    border-bottom: solid 5px white !important;
    background-color: white !important;
    color: #000;
    padding: 0;
  }

  // Clear date icon
  & .react-datepicker__close-icon {
    ${props =>
      props.showClearButton &&
      css`
        height: 3.75rem;
        @media screen and (min-width: ${theme.breakpoint.lg}px) {
          height: 3.125rem;
        }
        &::after {
          padding-top: 5px;
          background-color: transparent;
          content: url("data:image/svg+xml,%3Csvg width='22' height='22' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM6.4644 13.5356C6.07388 13.1451 6.07388 12.5119 6.4644 12.1214L8.58576 10L6.4645 7.87876C6.07397 7.48824 6.07397 6.85507 6.4645 6.46455C6.85502 6.07402 7.48818 6.07402 7.87871 6.46455L9.99997 8.58581L12.1213 6.46452C12.5118 6.074 13.1449 6.074 13.5355 6.46452C13.926 6.85505 13.926 7.48821 13.5355 7.87874L11.4142 10L13.5356 12.1214C13.9261 12.5119 13.9261 13.1451 13.5356 13.5356C13.145 13.9261 12.5119 13.9261 12.1213 13.5356L9.99997 11.4142L7.87862 13.5356C7.48809 13.9261 6.85493 13.9261 6.4644 13.5356Z' fill='%239D9D9C'/%3E%3C/svg%3E%0A");
          outline: none;
        }
      `}
  }

  // Day names in header
  & .react-datepicker__day-names {
    text-transform: capitalize;
    width: 230px;
  }

  // All choosable days for that month
  & .react-datepicker__month {
    margin: 0;
    border-bottom: ${props => (props.useUntilFurtherNotice ? '1px solid #dadada' : 'none')};
  }

  & .react-datepicker__day--outside-month {
    color: ${theme.colors.black50};
  }

  & .react-datepicker__month-container {
    padding: 10px 20px;
  }

  // Day specific styling
  & .react-datepicker__day {
    width: 32px;
    height: 32px;
    border-radius: 7px;
    margin: 0;
    line-height: 2rem;
    :hover {
      background-color: ${theme.colors.blueHover};
    }
    @media screen and (max-width: ${theme.breakpoint.md}px) {
      width: 40px;
      height: 40px;
    }
  }

  // Må, Ti...
  & .react-datepicker__day-name {
    width: 32px;
    height: 32px;
    margin: 0;
    @media screen and (max-width: ${theme.breakpoint.md}px) {
      width: 40px;
      height: 40px;
    }
  }

  & .react-datepicker__day.react-datepicker__day--selected {
    border: none;
    border-radius: 7px;
    background-color: ${theme.colors.black70};
    color: white;
  }

  & .react-datepicker__day.react-datepicker__day--keyboard-selected {
    border: none;
    color: #000;
    background: #e4e4e4;
  }

  & .react-datepicker__day.react-datepicker__day--keyboard-selected:hover {
    border: none;
    color: white;
    background-color: ${theme.colors.black70};
  }

  & .react-datepicker__day--disabled {
    color: ${theme.colors.black50};
    :hover {
      background-color: white;
    }
  }

  ${props => props.width && createResponsiveSpacingStyle('width', props.width)};
`;

const StyledInputWrapper = styled.div<{ width?: ResponsiveSpacing; labelBackground?: string }>`
  ${props => props.width && createResponsiveSpacingStyle('width', props.width)};
  background: ${props => (props.labelBackground ? getColor(props.labelBackground) : theme.colors.white)};
  width: 100%;
`;

const StyledCalendarHeader = styled.div`
  font-weight: 600;
  background-color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 230px;
  height: 30px;
  text-transform: capitalize;
  @media screen and (max-width: ${theme.breakpoint.md}px) {
    padding: 5px 20px 15px 20px;
  }
`;

const StyledHeaderNavigationArrow = styled.button`
  border: none;
  background: none;
  cursor: pointer;
`;

const StyledDivIconWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`;

type ForaDatePickerProps = {
  calendarDate: Date | null;
  setCalendarDate: Dispatch<SetStateAction<Date | null>>;
  errors: string[];
  label?: string;
  disabled?: boolean;
  /** Width of input field. Keep in mind that the calendar popup will always have min width 270px. */
  width?: ResponsiveSpacing;
  /** Lower range limit for selectable dates. Use YYYY-MM-DD */
  minDate?: string;
  /** Upper range limit for selectable dates. Use YYYY-MM-DD */
  maxDate?: string;
  /** What date the calendar opens up to. Default is today's date. */
  openToDate?: string;
  /** Show the "until further notice" text if no date is selected */
  useUntilFurtherNotice?: boolean;
  /** Default background is white */
  labelBackground?: Colors;
  /** To hide label when input is focused */
  hideLabelOnFocus?: boolean;
  /** To validate */
  onBlur: () => void;
  /** To validate */
  onCalendarClose: () => void;
  /** Aria-label */
  ariaLabel?: string;
  /** Custom test id */
  testId?: string;
};

export const ForaDatePicker = ({
  calendarDate,
  setCalendarDate,
  errors,
  label,
  disabled,
  width = 270,
  minDate = undefined,
  maxDate = undefined,
  openToDate,
  useUntilFurtherNotice = false,
  labelBackground,
  onBlur,
  onCalendarClose,
  hideLabelOnFocus = false,
  ariaLabel,
  testId,
}: ForaDatePickerProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const datepickerRef = useRef<any>();

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
    onCalendarClose();
  };

  const toggleCalendar = () => {
    if (datepickerRef.current) {
      if (isCalendarOpen) {
        datepickerRef.current.setOpen(false);
      } else {
        datepickerRef.current.setOpen(true);
      }
    }
  };

  const DatepickerContainer = ({ className, children }: { className: string; children: React.ReactNode }) => {
    const untilFurtherNoticeClick = () => {
      setCalendarDate(null);
      toggleCalendar();
    };

    return (
      <Box>
        <CalendarContainer className={className}>
          <Box>{children}</Box>
          {useUntilFurtherNotice && (
            <Box style={{ textAlign: 'center' }} paddingBottom={15}>
              <Button styleAs="link" onClick={untilFurtherNoticeClick}>
                {UNTIL_FURTHER_NOTICE}
              </Button>
            </Box>
          )}
        </CalendarContainer>
      </Box>
    );
  };

  return (
    <Wrapper showClearButton={!!calendarDate} width={width} useUntilFurtherNotice={useUntilFurtherNotice}>
      <Label
        labelColor="#6F6F6F"
        hasError={!!errors.length}
        id={`date-picker-input-field${testId ? '-' + testId : ''}`}
      >
        {label}
      </Label>
      <DatePicker
        calendarContainer={DatepickerContainer}
        onCalendarClose={handleCalendarClose}
        onCalendarOpen={() => setIsCalendarOpen(true)}
        customInput={
          <CustomInput
            calendarOpen={isCalendarOpen}
            toggleCalendar={toggleCalendar}
            errors={errors}
            label={label}
            width={width}
            useUntilFurtherNotice={useUntilFurtherNotice}
            labelBackground={labelBackground}
            hideLabelOnFocus={hideLabelOnFocus}
            ariaLabel={ariaLabel}
            testId={testId}
          />
        }
        ref={datepickerRef}
        // The first format in the list will be the one displayed, but all of them can be parsed
        dateFormat={['yyyy-MM-dd', 'yyyyMMdd', 'yyMMdd', 'yy-MM-dd']}
        disabled={disabled}
        isClearable={!disabled}
        locale="sv"
        maxDate={maxDate ? new Date(maxDate) : undefined}
        minDate={minDate ? new Date(minDate) : undefined}
        onChange={setCalendarDate}
        onBlur={onBlur}
        popperProps={{
          // https://github.com/Hacker0x01/react-datepicker/issues/4564
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          middleware: [flip({ mainAxis: false })],
        }}
        chooseDayAriaLabelPrefix="Välj"
        disabledDayAriaLabelPrefix="Inte tillgänglig"
        popperPlacement="bottom-start"
        ariaLabelClose="Rensa" // Close is actually clear
        renderCustomHeader={renderCustomHeader}
        selected={calendarDate}
        openToDate={openToDate ? new Date(openToDate) : undefined}
      />
      {!!errors.length &&
        errors.map((error, index) => (
          <ErrorMessage key={index} testId={`${testId}-${index}-error`} variant={'light'}>
            {error}
          </ErrorMessage>
        ))}
    </Wrapper>
  );
};

const renderCustomHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: ReactDatePickerCustomHeaderProps) => {
  const dateTitle = `${new Intl.DateTimeFormat('sv-SE', {
    month: 'long',
  }).format(date)} ${new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
  }).format(date)}`;
  return (
    <StyledCalendarHeader>
      <StyledHeaderNavigationArrow
        aria-label={(prevMonthButtonDisabled ? 'Inte tillgänglig ' : '') + 'Föregående månad'}
        onClick={e => {
          decreaseMonth();
          e.preventDefault();
        }}
        disabled={prevMonthButtonDisabled}
      >
        <ChevronIconOld color={prevMonthButtonDisabled ? 'black50' : 'black'} direction="left" width={10} />
      </StyledHeaderNavigationArrow>
      <Text fontWeight="semibold">{dateTitle}</Text>
      <StyledHeaderNavigationArrow
        aria-label={(nextMonthButtonDisabled ? 'Inte tillgänglig ' : '') + 'Nästa månad'}
        onClick={e => {
          increaseMonth();
          e.preventDefault();
        }}
        disabled={nextMonthButtonDisabled}
      >
        <ChevronIconOld color={nextMonthButtonDisabled ? 'black50' : 'black'} direction="right" width={10} />
      </StyledHeaderNavigationArrow>
    </StyledCalendarHeader>
  );
};

// There's no type for react-datepicker's custom input props.
// If we define a type for the props inherited from react-datepicker,
// the CustomInput component will complain that we're not explicitly assigning them.
// So any is used intentionally here for the union type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CustomInputProps = Pick<
  ForaDatePickerProps,
  'errors' | 'label' | 'useUntilFurtherNotice' | 'width' | 'labelBackground'
> & {
  toggleCalendar: () => void;
  calendarOpen: boolean;
  value?: string; // From react-datepicker input props
  disabled?: boolean; // From react-datepicker input props
  datePickerInputProps?: any; // From react-datepicker input props
  hideLabelOnFocus: boolean;
  ariaLabel?: string;
  testId?: string;
};

const CustomInput = forwardRef(
  (
    {
      errors,
      label,
      width,
      useUntilFurtherNotice = false,
      value: datePickerValue = '',
      labelBackground,
      toggleCalendar,
      calendarOpen,
      disabled: datePickerDisabled,
      hideLabelOnFocus = false,
      ariaLabel,
      testId,
      ...datePickerInputProps
    }: CustomInputProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    // We need to manipulate the input field behavior somewhat to get the "Tills vidare" UI
    const isUntilFurtherNotice = useUntilFurtherNotice && !datePickerValue;
    const displayedInputFieldValue = isUntilFurtherNotice && !calendarOpen ? UNTIL_FURTHER_NOTICE : datePickerValue;
    const placeHolderText = isUntilFurtherNotice && calendarOpen ? UNTIL_FURTHER_NOTICE : undefined;

    return (
      <StyledInputWrapper width={width} labelBackground={labelBackground}>
        <InputField
          className="input-field"
          errors={errors}
          icon={
            datePickerValue || datePickerDisabled ? null : (
              // onMouseDown to stop onBlur close calender at the same time as toggle close
              <StyledDivIconWrapper onMouseDown={e => e.stopPropagation()} onClick={toggleCalendar} role="button">
                <AnimatedChevron calendarOpen={calendarOpen} />
              </StyledDivIconWrapper>
            )
          }
          isActive={datePickerDisabled ? false : !!datePickerValue || calendarOpen}
          inputMode="none"
          hideLabel
          hideInputError
          label={label || 'Startdatum'}
          preambleIcon={
            // onMouseDown to stop onBlur close calender at the same time as toggle close
            <StyledDivIconWrapper
              onMouseDown={e => e.stopPropagation()}
              onClick={!datePickerDisabled ? toggleCalendar : undefined}
              role="button"
              style={{ cursor: datePickerDisabled ? 'not-allowed' : 'auto' }}
            >
              <CalendarIcon width={22} color="black50" />
            </StyledDivIconWrapper>
          }
          type="text"
          innerRef={ref}
          style={isUntilFurtherNotice ? { color: theme.colors.black70 } : undefined}
          /*** react-datepicker <input> common props ***/
          // the props spread here contains inherited event handlers from react-datepicker
          {...datePickerInputProps}
          id={`date-picker-input-field${testId ? '-' + testId : ''}`}
          disabled={datePickerDisabled}
          placeholder={placeHolderText}
          value={displayedInputFieldValue}
          hideLabelOnFocus={hideLabelOnFocus}
          ariaLabel={ariaLabel}
          isDatePicker
        />
      </StyledInputWrapper>
    );
  }
);

const UNTIL_FURTHER_NOTICE = 'Tills vidare';

const AnimatedChevron = ({ calendarOpen }: { calendarOpen: boolean }) => {
  const isTablet = useMediaMin(theme.breakpoint.md);
  return (
    <Animate.Rotate isTriggered={calendarOpen} degrees={180}>
      <ChevronIconOld variant="thin" direction="downward" width={isTablet ? 14 : 16} containerSize={20} />
    </Animate.Rotate>
  );
};
